import React, { useState, useEffect } from 'react';
import { dashboardService } from '../../../services/dashboardService';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FloorPlanManagementPanel = () => {
  const [activeTab, setActiveTab] = useState('plans');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [floorPlans, setFloorPlans] = useState([]);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [commissionRanges, setCommissionRanges] = useState([]);
  
  const [formData, setFormData] = useState({
    projectName: 'Residencial 3 Volcanes',
    lotNumber: '',
    basePrice: '',
    discountPercentage: '0',
    status: 'available',
    name: '',
    description: '',
    pointsRequired: '',
    rewardType: 'bonus',
    rewardValue: '',
    rangeName: '',
    minSales: '',
    maxSales: '',
    commissionPercentage: '',
    bonusAmount: '0',
    level: '1'
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'plans') {
        const plans = await dashboardService?.getAllFloorPlans();
        setFloorPlans(plans);
      } else if (activeTab === 'prices') {
        // Get pricing tiers for all floor plans
        const plans = await dashboardService?.getAllFloorPlans();
        if (plans?.length > 0) {
          const tiers = await dashboardService?.getPricingTiers(plans?.[0]?.id);
          setPricingTiers(tiers);
        }
      } else if (activeTab === 'rewards') {
        const { data, error } = await supabase?.from('rewards')?.select('*')?.order('points_required', { ascending: true });
        if (!error) setRewards(data || []);
      } else if (activeTab === 'ranges') {
        const ranges = await dashboardService?.getCommissionRanges();
        setCommissionRanges(ranges);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    try {
      if (activeTab === 'plans') {
        // Create or update floor plan
        const planData = {
          project_name: 'Residencial 3 Volcanes',
          sections: [],
          lots: []
        };
        
        if (editingItem) {
          await supabase?.from('floor_plans')?.update(planData)?.eq('id', editingItem?.id);
        } else {
          await supabase?.from('floor_plans')?.insert(planData);
        }
      } else if (activeTab === 'prices') {
        // Create or update pricing tier
        const plans = await dashboardService?.getAllFloorPlans();
        if (plans?.length > 0) {
          await dashboardService?.upsertPricingTier({
            id: editingItem?.id,
            floorPlanId: plans?.[0]?.id,
            lotNumber: formData?.lotNumber,
            basePrice: parseFloat(formData?.basePrice),
            discountPercentage: parseFloat(formData?.discountPercentage),
            status: formData?.status
          });
        }
      } else if (activeTab === 'rewards') {
        // Create or update reward
        const rewardData = {
          name: formData?.name,
          description: formData?.description,
          points_required: parseInt(formData?.pointsRequired),
          reward_type: formData?.rewardType,
          reward_value: parseFloat(formData?.rewardValue),
          is_active: true
        };
        
        if (editingItem) {
          await supabase?.from('rewards')?.update(rewardData)?.eq('id', editingItem?.id);
        } else {
          await supabase?.from('rewards')?.insert(rewardData);
        }
      } else if (activeTab === 'ranges') {
        // Create or update commission range
        const rangeData = {
          range_name: formData?.rangeName,
          min_sales: parseInt(formData?.minSales),
          max_sales: formData?.maxSales ? parseInt(formData?.maxSales) : null,
          commission_percentage: parseFloat(formData?.commissionPercentage),
          bonus_amount: parseFloat(formData?.bonusAmount),
          level: parseInt(formData?.level),
          is_active: true
        };
        
        if (editingItem) {
          await supabase?.from('commission_ranges')?.update(rangeData)?.eq('id', editingItem?.id);
        } else {
          await supabase?.from('commission_ranges')?.insert(rangeData);
        }
      }
      
      // Reload data and close modal
      await loadData();
      setShowAddModal(false);
      setEditingItem(null);
      resetForm();
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    
    if (activeTab === 'plans') {
      setFormData({ ...formData, projectName: item?.projectName });
    } else if (activeTab === 'prices') {
      setFormData({
        ...formData,
        lotNumber: item?.lotNumber,
        basePrice: item?.basePrice?.toString(),
        discountPercentage: item?.discountPercentage?.toString(),
        status: item?.status
      });
    } else if (activeTab === 'rewards') {
      setFormData({
        ...formData,
        name: item?.name,
        description: item?.description,
        pointsRequired: item?.points_required?.toString(),
        rewardType: item?.reward_type,
        rewardValue: item?.reward_value?.toString()
      });
    } else if (activeTab === 'ranges') {
      setFormData({
        ...formData,
        rangeName: item?.rangeName || item?.range_name,
        minSales: item?.minSales?.toString() || item?.min_sales?.toString(),
        maxSales: item?.maxSales?.toString() || item?.max_sales?.toString() || '',
        commissionPercentage: item?.commissionPercentage?.toString() || item?.commission_percentage?.toString(),
        bonusAmount: item?.bonusAmount?.toString() || item?.bonus_amount?.toString(),
        level: item?.level?.toString()
      });
    }
    
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Estás seguro de eliminar este elemento?')) return;
    
    try {
      if (activeTab === 'plans') {
        await supabase?.from('floor_plans')?.delete()?.eq('id', id);
      } else if (activeTab === 'prices') {
        await dashboardService?.deletePricingTier(id);
      } else if (activeTab === 'rewards') {
        await supabase?.from('rewards')?.delete()?.eq('id', id);
      } else if (activeTab === 'ranges') {
        await supabase?.from('commission_ranges')?.delete()?.eq('id', id);
      }
      
      await loadData();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      projectName: 'Residencial 3 Volcanes',
      lotNumber: '',
      basePrice: '',
      discountPercentage: '0',
      status: 'available',
      name: '',
      description: '',
      pointsRequired: '',
      rewardType: 'bonus',
      rewardValue: '',
      rangeName: '',
      minSales: '',
      maxSales: '',
      commissionPercentage: '',
      bonusAmount: '0',
      level: '1'
    });
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground flex items-center">
          <Icon name="FileEdit" size={24} className="mr-2 text-primary" />
          Gestión de Planos y Precios
        </h2>
        <Button
          iconName="Plus"
          onClick={() => {
            setEditingItem(null);
            resetForm();
            setShowAddModal(true);
          }}
          size="sm"
        >
          Agregar Nuevo
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6 border-b border-border">
        <button
          onClick={() => setActiveTab('plans')}
          className={`px-4 py-2 font-medium transition-smooth ${
            activeTab === 'plans' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Planos
        </button>
        <button
          onClick={() => setActiveTab('prices')}
          className={`px-4 py-2 font-medium transition-smooth ${
            activeTab === 'prices' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Precios
        </button>
        <button
          onClick={() => setActiveTab('rewards')}
          className={`px-4 py-2 font-medium transition-smooth ${
            activeTab === 'rewards' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Premios
        </button>
        <button
          onClick={() => setActiveTab('ranges')}
          className={`px-4 py-2 font-medium transition-smooth ${
            activeTab === 'ranges' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Rangos
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-4">
          {activeTab === 'plans' && (
            <div className="space-y-3">
              {floorPlans?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No hay planos registrados</p>
              ) : (
                floorPlans?.map((plan) => (
                  <div key={plan?.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">{plan?.projectName}</p>
                      <p className="text-sm text-muted-foreground">{plan?.lots?.length || 0} lotes</p>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" iconName="Edit" onClick={() => handleEdit(plan)}>
                        Editar
                      </Button>
                      <Button variant="outline" size="sm" iconName="Trash2" onClick={() => handleDelete(plan?.id)}>
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'prices' && (
            <div className="space-y-3">
              {pricingTiers?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No hay precios configurados</p>
              ) : (
                pricingTiers?.map((tier) => (
                  <div key={tier?.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">Lote {tier?.lotNumber}</p>
                      <p className="text-sm text-muted-foreground">
                        S/ {tier?.finalPrice?.toLocaleString()} ({tier?.discountPercentage}% desc.) - {tier?.status}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" iconName="Edit" onClick={() => handleEdit(tier)}>
                        Modificar
                      </Button>
                      <Button variant="outline" size="sm" iconName="Trash2" onClick={() => handleDelete(tier?.id)}>
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'rewards' && (
            <div className="space-y-3">
              {rewards?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No hay premios configurados</p>
              ) : (
                rewards?.map((reward) => (
                  <div key={reward?.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">{reward?.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {reward?.points_required} puntos - S/ {reward?.reward_value?.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" iconName="Edit" onClick={() => handleEdit(reward)}>
                        Modificar
                      </Button>
                      <Button variant="outline" size="sm" iconName="Trash2" onClick={() => handleDelete(reward?.id)}>
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'ranges' && (
            <div className="space-y-3">
              {commissionRanges?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No hay rangos configurados</p>
              ) : (
                commissionRanges?.map((range) => (
                  <div key={range?.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">{range?.rangeName}</p>
                      <p className="text-sm text-muted-foreground">
                        {range?.minSales}-{range?.maxSales || '∞'} ventas - {range?.commissionPercentage}% comisión
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" iconName="Edit" onClick={() => handleEdit(range)}>
                        Modificar
                      </Button>
                      <Button variant="outline" size="sm" iconName="Trash2" onClick={() => handleDelete(range?.id)}>
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="bg-card p-6 rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                {editingItem ? 'Editar' : 'Agregar'} {activeTab === 'plans' ? 'Plano' : activeTab === 'prices' ? 'Precio' : activeTab === 'rewards' ? 'Premio' : 'Rango'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingItem(null);
                  resetForm();
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              {activeTab === 'plans' && (
                <Input
                  label="Nombre del Proyecto"
                  value={formData?.projectName}
                  onChange={(e) => setFormData({ ...formData, projectName: e?.target?.value })}
                  required
                />
              )}
              
              {activeTab === 'prices' && (
                <>
                  <Input
                    label="Número de Lote"
                    value={formData?.lotNumber}
                    onChange={(e) => setFormData({ ...formData, lotNumber: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Precio Base"
                    type="number"
                    step="0.01"
                    value={formData?.basePrice}
                    onChange={(e) => setFormData({ ...formData, basePrice: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Descuento (%)"
                    type="number"
                    step="0.01"
                    value={formData?.discountPercentage}
                    onChange={(e) => setFormData({ ...formData, discountPercentage: e?.target?.value })}
                  />
                  <Select
                    label="Estado"
                    value={formData?.status}
                    onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
                  >
                    <option value="available">Disponible</option>
                    <option value="reserved">Reservado</option>
                    <option value="sold">Vendido</option>
                  </Select>
                </>
              )}
              
              {activeTab === 'rewards' && (
                <>
                  <Input
                    label="Nombre del Premio"
                    value={formData?.name}
                    onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Descripción"
                    value={formData?.description}
                    onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  />
                  <Input
                    label="Puntos Requeridos"
                    type="number"
                    value={formData?.pointsRequired}
                    onChange={(e) => setFormData({ ...formData, pointsRequired: e?.target?.value })}
                    required
                  />
                  <Select
                    label="Tipo de Premio"
                    value={formData?.rewardType}
                    onChange={(e) => setFormData({ ...formData, rewardType: e?.target?.value })}
                  >
                    <option value="bonus">Bono</option>
                    <option value="gift">Regalo</option>
                    <option value="trip">Viaje</option>
                    <option value="recognition">Reconocimiento</option>
                    <option value="training">Capacitación</option>
                  </Select>
                  <Input
                    label="Valor del Premio"
                    type="number"
                    step="0.01"
                    value={formData?.rewardValue}
                    onChange={(e) => setFormData({ ...formData, rewardValue: e?.target?.value })}
                    required
                  />
                </>
              )}
              
              {activeTab === 'ranges' && (
                <>
                  <Input
                    label="Nombre del Rango"
                    value={formData?.rangeName}
                    onChange={(e) => setFormData({ ...formData, rangeName: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Ventas Mínimas"
                    type="number"
                    value={formData?.minSales}
                    onChange={(e) => setFormData({ ...formData, minSales: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Ventas Máximas (opcional)"
                    type="number"
                    value={formData?.maxSales}
                    onChange={(e) => setFormData({ ...formData, maxSales: e?.target?.value })}
                  />
                  <Input
                    label="Porcentaje de Comisión"
                    type="number"
                    step="0.01"
                    value={formData?.commissionPercentage}
                    onChange={(e) => setFormData({ ...formData, commissionPercentage: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Bono Adicional"
                    type="number"
                    step="0.01"
                    value={formData?.bonusAmount}
                    onChange={(e) => setFormData({ ...formData, bonusAmount: e?.target?.value })}
                  />
                  <Input
                    label="Nivel"
                    type="number"
                    value={formData?.level}
                    onChange={(e) => setFormData({ ...formData, level: e?.target?.value })}
                    required
                  />
                </>
              )}
              
              <div className="flex space-x-3">
                <Button type="submit" fullWidth>
                  {editingItem ? 'Actualizar' : 'Guardar'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingItem(null);
                    resetForm();
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorPlanManagementPanel;